// Task lane for one AI employee (2026-11-04, Magnus: "make it board based so I can assign new
// tasks and have it run").
//
// 派工: one line to the employee becomes its own thread (POST /api/agent-threads/team/:key/tasks),
// queued at once. Each task then shows where it is — 排隊中 / 進行中 / 等你批准 / 等你回覆 / 做不到 /
// 完成 — and, when done, the 證據 line the employee had to end with. 完成 without 證據 shows as
// 「說完成·沒附證據」: done means observed. Open a task to read the report, reply (follow-ups go into
// the same thread) or 收下 (archive) it.

import React, { useState } from 'react';
import { Box, Button, CircularProgress, Collapse, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import api from '../../services/api';
import { RichText, ago, errMessage } from './agentShared';
import { TASK_STATE, Task, assignTask } from './employeeShared';

export const TaskPill: React.FC<{ t: Pick<Task, 'state'> }> = ({ t }) => {
  const s = TASK_STATE[t.state] || TASK_STATE.needs_you;
  return (
    <Box component="span" sx={{ bgcolor: s.bg, color: s.fg, fontSize: 11, fontWeight: 700, px: 1, py: 0.25, borderRadius: 99,
      whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      {(t.state === 'running' || t.state === 'queued') && <AutorenewRoundedIcon sx={{ fontSize: 13 }} />}
      {s.label}
    </Box>
  );
};

/** The 派工 box: one line → a queued task. Used on the employee page and on the team page. */
export const AssignBox: React.FC<{
  empKey: string | null;
  name?: string;
  placeholder?: string;
  onAssigned: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ empKey, name, placeholder, onAssigned, onError }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const t = text.trim();
    if (!t || !empKey) return;
    setBusy(true);
    try {
      await assignTask(empKey, t);
      setText('');
      onAssigned(`已派給${name || '他'}，馬上開始`);
    } catch (err) {
      onError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
      <TextField
        fullWidth
        multiline
        maxRows={5}
        size="small"
        disabled={!empKey}
        placeholder={placeholder || `派工給${name || ''}…（寫一件事，做完他會附上證據）`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
      />
      <Button variant="contained" disableElevation disabled={busy || !text.trim() || !empKey} onClick={send}
        sx={{ flexShrink: 0, borderRadius: 3, minWidth: 64 }}>
        {busy ? <CircularProgress size={18} color="inherit" /> : '派工'}
      </Button>
    </Box>
  );
};

const TaskRow: React.FC<{ t: Task; onChanged: (msg?: string) => void; onError: (msg: string) => void }> = ({
  t,
  onChanged,
  onError,
}) => {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const live = t.state === 'running' || t.state === 'queued';
  const run = async (fn: () => Promise<unknown>, msg?: string) => {
    setBusy(true);
    try {
      await fn();
      onChanged(msg);
      return true;
    } catch (err) {
      onError(errMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const sendReply = () =>
    run(() => api.post(`/api/agent-threads/${t.id}/messages`, { text: reply.trim() }), '已回覆，他接著做').then(
      (ok) => ok && setReply('')
    );
  const line =
    live ? t.status_line || '工作中…'
    : t.state === 'approval' ? '卡片在上面「等你決定」'
    : t.evidence ? `${t.state === 'done' ? '證據' : '他說'}：${t.evidence}`
    : t.state === 'unproven' ? '他說完成了，但沒寫怎麼確認的。點開看，或叫他補證據'
    : t.state === 'failed' ? '這一輪沒跑完，點開回覆讓他再試'
    : '點開看他的回覆';
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, opacity: t.archived ? 0.75 : 1,
      borderLeft: '4px solid', borderLeftColor: TASK_STATE[t.state]?.fg || 'divider' }}>
      <Box role="button" onClick={() => setOpen((v) => !v)} sx={{ p: 1.25, cursor: 'pointer' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, lineHeight: 1.45, display: '-webkit-box',
            WebkitLineClamp: open ? 'none' : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
            {t.ask}
          </Typography>
          <TaskPill t={t} />
        </Box>
        <Typography variant="body2" sx={{ fontSize: 12.5, mt: 0.5, lineHeight: 1.5, wordBreak: 'break-word',
          color: t.state === 'done' ? '#3B6D11' : 'text.secondary' }}>
          {line}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {ago(t.created_at)}前派的{t.report ? ` · 回覆 ${ago(t.report.created_at)}前` : ''}
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.25, pb: 1.25 }}>
          {t.report ? (
            <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 1.25, mb: 1 }}>
              <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7, fontSize: 13.5 }}>
                <RichText text={t.report.content} />
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>還沒有回覆。</Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
            <TextField fullWidth multiline maxRows={5} size="small" value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder={t.state === 'unproven' ? '例：補上讀回來的數字' : '回覆這個任務…（他會接著做）'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && reply.trim()) sendReply();
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
            <IconButton color="primary" aria-label="回覆任務" disabled={busy || !reply.trim()} onClick={sendReply}>
              {busy ? <CircularProgress size={20} /> : <SendRoundedIcon />}
            </IconButton>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {!live && !t.archived && (
              <Button size="small" variant={t.state === 'done' ? 'contained' : 'outlined'} disableElevation disabled={busy}
                color={t.state === 'done' ? 'primary' : 'inherit'}
                onClick={() => run(() => api.patch(`/api/agent-threads/${t.id}`, { archived: true }), '已收下')}>
                {t.state === 'done' ? '收下' : '結案'}
              </Button>
            )}
            {t.state === 'unproven' && (
              <Button size="small" variant="outlined" disabled={busy}
                onClick={() => run(() => api.post(`/api/agent-threads/${t.id}/messages`, {
                  text: '你說完成了，但沒有證據。去讀回確認（GET / SELECT / 讀檔 / 看圖），最後兩行照格式：任務狀態、證據。',
                }), '已叫他補證據')}>
                叫他補證據
              </Button>
            )}
            {live && (
              <Button size="small" color="inherit" disabled={busy}
                onClick={() => run(() => api.post(`/api/agent-threads/${t.id}/stop`), '已叫他停')}>
                停下來
              </Button>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

/** The 任務 section on an employee page. */
const EmployeeTasks: React.FC<{
  empKey: string;
  name: string;
  tasks: Task[];
  onChanged: (msg?: string) => void;
  onError: (msg: string) => void;
}> = ({ empKey, name, tasks, onChanged, onError }) => {
  const [showClosed, setShowClosed] = useState(false);
  const open = tasks.filter((t) => !t.archived);
  const closed = tasks.filter((t) => t.archived);
  return (
    <Box>
      <AssignBox empKey={empKey} name={name} onAssigned={(m) => onChanged(m)} onError={onError} />
      <Stack spacing={1} sx={{ mt: 1.25 }}>
        {open.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            沒有進行中的任務。寫一件事派給{name}，他會用自己的權限做，做完附上證據。
          </Typography>
        )}
        {open.map((t) => <TaskRow key={t.id} t={t} onChanged={onChanged} onError={onError} />)}
      </Stack>
      {closed.length > 0 && (
        <>
          <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowClosed((v) => !v)}>
            {showClosed ? '收起' : `已收下 ${closed.length}`}
          </Button>
          <Collapse in={showClosed} unmountOnExit>
            <Stack spacing={1}>
              {closed.map((t) => <TaskRow key={t.id} t={t} onChanged={onChanged} onError={onError} />)}
            </Stack>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default EmployeeTasks;
